// src/utils/layoutHelpers.js
// Layout configuration and helper functions for responsive design with fixed sections

// === LAYOUT CONFIGURATION FOR FIXED SECTIONS ===
export const LAYOUT_CONFIGS = {
    desktop: {
        header: { top: 0, height: '20%' },
        content: { top: '20%', height: '60%' },
        widget: { position: 'right', widthPx: 400, heightPx: 600, marginPx: 20 },
        footer: { bottom: 0, height: '20%' }
    },
    tablet_portrait: {
        header: { top: 0, height: '15%' },
        content: { top: '15%', height: '50%' },
        widget: { position: 'bottom', widthPercent: 90, heightPx: 400, marginPx: 10 },
        footer: { bottom: 0, height: '35%' }
    },
    tablet_landscape: {
        header: { top: 0, height: '12%' },
        content: { top: '12%', height: '40%', leftPercent: 0, widthPercent: 50 },
        widget: { position: 'right', widthPercent: 45, heightPercent: 80, marginPx: 10 },
        footer: { bottom: 0, height: '48%' }
    },
    mobile_portrait: {
        header: { top: 0, height: '12%' },
        content: { top: '12%', height: '45%' },
        widget: { position: 'bottom', widthPercent: 95, heightPx: 350, marginPx: 5 },
        footer: { bottom: 0, height: '43%' }
    },
    mobile_landscape: {
        header: { top: 0, height: '8%' },
        content: { top: '8%', height: '30%' },
        widget: { position: 'bottom-right', widthPercent: 55, heightPx: 180, marginPx: 5 },
        footer: { bottom: 0, height: '62%' }
    }
};

// === EXTENSIVE DEVICE & RESOLUTION CLASSIFIER ===
export const getExtendedDeviceProfile = (width, height) => {
    const isLandscape = width > height;
    const aspectRatio = width / (height || 1);

    // Foldables detection
    const isFoldableCover = (width <= 360 && aspectRatio < 0.45) || (height <= 360 && aspectRatio > 2.3);
    const isFoldableOpen = (width >= 700 && width <= 1440) && (aspectRatio >= 0.85 && aspectRatio <= 1.25);

    let deviceCategory = 'desktop_standard';
    let legacyKey = 'default';

    if (isFoldableCover) {
        deviceCategory = isLandscape ? 'mobile_foldable_landscape' : 'mobile_foldable_portrait';
        legacyKey = isLandscape ? 'zFoldLand' : 'zFold';
    } else if (isFoldableOpen) {
        deviceCategory = isLandscape ? 'foldable_landscape' : 'foldable_portrait';
        legacyKey = isLandscape ? 'zenbookFoldLand' : 'zenbookFold';
    } else if (width >= 1800) {
        deviceCategory = 'desktop_fhd';
        legacyKey = 'fhd';
    } else if (width >= 1440) {
        deviceCategory = 'desktop_standard';
        legacyKey = 'desktop';
    } else if (width > 1024) {
        // Laptop / Mac / Ventanas compactas de escritorio con DevTools (1025px - 1439px)
        if (isLandscape) {
            deviceCategory = 'laptop_mac';
            legacyKey = 'mac';
        } else {
            deviceCategory = 'tablet_large_portrait';
            legacyKey = 'ipadPro';
        }
    } else if (width >= 768) {
        // Tablets (iPad, iPad Pro, Surface, Galaxy Tab)
        if (isLandscape) {
            deviceCategory = 'tablet_landscape';
            legacyKey = (width >= 1024) ? 'ipadPro' : (width >= 900 ? 'surfacePro7Land' : 'ipad');
        } else {
            deviceCategory = 'tablet_portrait';
            legacyKey = (height >= 1300) ? 'ipadPro' : (height >= 1100 ? 'surfacePro7' : 'ipad');
        }
    } else {
        // Mobile devices (< 768px)
        if (isLandscape) {
            deviceCategory = 'mobile_landscape';
            if (width <= 670) legacyKey = 'seLand';
            else if (width <= 750) legacyKey = 's8Land';
            else if (width <= 850) legacyKey = 'i12Land';
            else if (width <= 900) legacyKey = 'xrLand';
            else if (width <= 920) legacyKey = 'pixel7Land';
            else legacyKey = 'i14ProLand';
        } else {
            deviceCategory = 'mobile_portrait';
            if (width <= 360) legacyKey = 's8';
            else if (width <= 375) legacyKey = 'se';
            else if (width <= 390) legacyKey = 'i12';
            else if (width <= 412) legacyKey = 'pixel7';
            else if (width <= 414) legacyKey = 'xr';
            else if (width <= 430) legacyKey = 'i14ProMax';
            else legacyKey = 'mobile';
        }
    }

    const isMobile = width <= 768 || deviceCategory.startsWith('mobile');
    const isTablet = (width >= 768 && width <= 1368) || deviceCategory.startsWith('tablet');

    return {
        width,
        height,
        isLandscape,
        aspectRatio,
        deviceCategory,
        legacyKey,
        isMobile,
        isTablet
    };
};

// === DEVICE DETECTION ===
export const getDeviceType = (width, height) => {
    const profile = getExtendedDeviceProfile(width, height);
    if (profile.deviceCategory.startsWith('desktop') || profile.deviceCategory === 'laptop_mac') {
        return 'desktop';
    }
    if (profile.deviceCategory.startsWith('tablet')) {
        return profile.isLandscape ? 'tablet_landscape' : 'tablet_portrait';
    }
    return profile.isLandscape ? 'mobile_landscape' : 'mobile_portrait';
};

// === WIDGET ZONE CALCULATION ===
export const calculateWidgetZone = (widgetConfig, screenWidth, screenHeight) => {
    const margin = widgetConfig.marginPx || 10;

    let width = widgetConfig.widthPx ||
        (widgetConfig.widthPercent ? (widgetConfig.widthPercent / 100) * screenWidth : 400);
    let height = widgetConfig.heightPx ||
        (widgetConfig.heightPercent ? (widgetConfig.heightPercent / 100) * screenHeight : 600);

    let x, y;

    switch (widgetConfig.position) {
        case 'right':
            x = screenWidth - width - margin;
            y = (screenHeight - height) / 2;
            break;
        case 'left':
            x = margin;
            y = (screenHeight - height) / 2;
            break;
        case 'bottom':
            x = (screenWidth - width) / 2;
            y = screenHeight - height - margin;
            break;
        case 'bottom-right':
            x = screenWidth - width - margin;
            y = screenHeight - height - margin;
            break;
        case 'bottom-left':
            x = margin;
            y = screenHeight - height - margin;
            break;
        default:
            x = (screenWidth - width) / 2;
            y = (screenHeight - height) / 2;
    }

    return {
        x: Math.max(margin, x),
        y: Math.max(margin, y),
        width: Math.min(width, screenWidth - 2 * margin),
        height: Math.min(height, screenHeight - 2 * margin)
    };
};

// === SAFE ZONE CALCULATION ===
export const calculateSafeZones = (deviceType, resolution) => {
    const config = LAYOUT_CONFIGS[deviceType];
    const { width, height } = resolution;

    const parseValue = (value, total) => {
        if (typeof value === 'string' && value.includes('%')) {
            return (parseInt(value) / 100) * total;
        }
        return value;
    };

    return {
        header: {
            top: 0,
            left: 0,
            width: width,
            height: parseValue(config.header.height, height)
        },
        content: {
            top: parseValue(config.content.top, height),
            left: config.content.leftPercent ? (config.content.leftPercent / 100) * width : 0,
            width: config.content.widthPercent ? (config.content.widthPercent / 100) * width : width,
            height: parseValue(config.content.height, height)
        },
        widget: calculateWidgetZone(config.widget, width, height),
        footer: {
            bottom: 0,
            left: 0,
            width: width,
            height: parseValue(config.footer.height, height)
        }
    };
};

// === GET TITLE SIZE FOR DEVICE TYPE ===
export const getTitleSizeForDevice = (deviceType, baseSize = 'default') => {
    const sizes = {
        mobile_landscape: {
            default: 'text-2xl md:text-3xl',
            large: 'text-3xl md:text-4xl',
            small: 'text-xl md:text-2xl'
        },
        mobile_portrait: {
            default: 'text-3xl md:text-4xl',
            large: 'text-4xl md:text-5xl',
            small: 'text-2xl md:text-3xl'
        },
        tablet_landscape: {
            default: 'text-3xl md:text-4xl',
            large: 'text-4xl md:text-5xl',
            small: 'text-2xl md:text-3xl'
        },
        tablet_portrait: {
            default: 'text-4xl md:text-5xl',
            large: 'text-5xl md:text-6xl',
            small: 'text-3xl md:text-4xl'
        },
        desktop: {
            default: 'text-4xl md:text-5xl',
            large: 'text-5xl md:text-6xl',
            small: 'text-3xl md:text-4xl'
        }
    };

    return sizes[deviceType]?.[baseSize] || sizes.desktop.default;
};

// === GET SUBTITLE SIZE FOR DEVICE TYPE ===
export const getSubtitleSizeForDevice = (deviceType, baseSize = 'default') => {
    const sizes = {
        mobile_landscape: {
            default: 'text-base md:text-lg',
            large: 'text-lg md:text-xl',
            small: 'text-sm md:text-base'
        },
        mobile_portrait: {
            default: 'text-lg md:text-xl',
            large: 'text-xl md:text-2xl',
            small: 'text-base md:text-lg'
        },
        tablet_landscape: {
            default: 'text-lg md:text-xl',
            large: 'text-xl md:text-2xl',
            small: 'text-base md:text-lg'
        },
        tablet_portrait: {
            default: 'text-xl md:text-2xl',
            large: 'text-2xl md:text-3xl',
            small: 'text-lg md:text-xl'
        },
        desktop: {
            default: 'text-2xl md:text-3xl',
            large: 'text-3xl md:text-4xl',
            small: 'text-xl md:text-2xl'
        }
    };

    return sizes[deviceType]?.[baseSize] || sizes.desktop.default;
};
